// src/theme/SubframeProvider.jsx - Subframe theme provider and configuration
import { createContext, useContext, useState, useEffect } from 'react';

// Theme configurations matching your Subframe setup
const themes = {
  dark: {
    name: 'dark',
    displayName: 'Dark Theme',
    colors: {
      background: 'rgb(3, 7, 18)', // default-background
      foreground: 'rgb(255, 255, 255)', // Pure white for maximum contrast
      primary: 'rgb(34, 211, 238)', // Brighter cyan for better visibility
      secondary: 'rgb(203, 213, 225)', // Much lighter gray for better readability
      accent: 'rgb(6, 182, 212)', // Brighter accent
      muted: 'rgb(30, 41, 59)', // Slightly lighter for better card visibility
      border: 'rgb(71, 85, 105)', // Lighter border
      card: 'rgb(30, 41, 59)', // Lighter card background
      input: 'rgb(51, 65, 85)', // Lighter input background
    },
    gradients: {
      primary: 'from-brand-400 to-brand-600',
      accent: 'from-brand-500 to-brand-700',
      hero: 'from-neutral-0 via-neutral-50 to-neutral-100',
    }
  },
  light: {
    name: 'light',
    displayName: 'Light Theme',
    colors: {
      background: 'rgb(255, 255, 255)', // neutral-950
      foreground: 'rgb(3, 7, 18)', // black
      primary: 'rgb(5, 162, 194)', // brand-primary
      secondary: 'rgb(107, 114, 128)', // neutral-400
      accent: 'rgb(0, 100, 125)', // brand-500
      muted: 'rgb(249, 250, 251)', // neutral-900
      border: 'rgb(229, 231, 235)', // neutral-700
      card: 'rgb(255, 255, 255)', // neutral-950
      input: 'rgb(249, 250, 251)', // neutral-900
    },
    gradients: {
      primary: 'from-brand-600 to-brand-800',
      accent: 'from-brand-700 to-brand-900',
      hero: 'from-neutral-900 via-neutral-800 to-neutral-700',
    }
  },
  journal: {
    name: 'journal',
    displayName: 'Journal Theme',
    colors: {
      background: 'rgb(7, 40, 48)', // brand-50
      foreground: 'rgb(255, 255, 255)', // Pure white for maximum readability
      primary: 'rgb(34, 211, 238)', // Brighter cyan for better visibility
      secondary: 'rgb(165, 243, 252)', // Much lighter teal for better contrast
      accent: 'rgb(6, 182, 212)', // Brighter accent
      muted: 'rgb(15, 56, 68)', // Lighter muted background
      border: 'rgb(21, 94, 117)', // Lighter border
      card: 'rgb(15, 62, 77)', // Lighter card background
      input: 'rgb(22, 78, 99)', // Lighter input background
    },
    gradients: {
      primary: 'from-brand-400 to-brand-600',
      accent: 'from-brand-600 to-brand-800',
      hero: 'from-brand-50 via-brand-100 to-brand-200',
    }
  }
};

const ThemeContext = createContext({
  theme: themes.dark,
  themeName: 'dark',
  setTheme: () => {},
  themes,
  toggleTheme: () => {},
});

export function SubframeProvider({ children }) {
  const [currentTheme, setCurrentTheme] = useState('dark');
  
  // Load theme from localStorage on mount
  // Wrapped in try-catch: WKWebView with ITP can throw on storage access.
  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem('subframe-theme') || 'dark';
      if (themes[savedTheme]) {
        setCurrentTheme(savedTheme);
      }
    } catch {
      // Storage unavailable — keep default 'dark' theme
    }
  }, []);

  // Update CSS variables when theme changes
  useEffect(() => {
    const theme = themes[currentTheme];
    const root = document.documentElement;
    
    // Apply theme colors as CSS custom properties
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--theme-${key}`, value);
    });
    
    // Apply theme class to body
    document.body.className = `theme-${currentTheme}`;
    
    // Save to localStorage (guarded: WKWebView with ITP can throw)
    try { localStorage.setItem('subframe-theme', currentTheme); } catch { /* ignore */ }
  }, [currentTheme]);

  const setTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
    }
  };

  const toggleTheme = () => {
    const themeNames = Object.keys(themes);
    const currentIndex = themeNames.indexOf(currentTheme);
    const nextIndex = (currentIndex + 1) % themeNames.length;
    setCurrentTheme(themeNames[nextIndex]);
  };

  const value = {
    theme: themes[currentTheme],
    themeName: currentTheme,
    setTheme,
    themes,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a SubframeProvider');
  }
  return context;
}

export function useSubframeClasses() {
  const { theme } = useTheme();
  
  return {
    // Layout classes
    page: `min-h-dvh w-full bg-[var(--theme-background)] text-[var(--theme-foreground)]`,
    container: 'w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
    card: `bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg shadow-sm`,
    input: `bg-[var(--theme-input)] border border-[var(--theme-border)] text-[var(--theme-foreground)] placeholder-[var(--theme-secondary)]`,
    
    // Typography classes using Subframe tokens
    h1: 'text-heading-1 font-heading-1 text-[var(--theme-foreground)]',
    h2: 'text-heading-2 font-heading-2 text-[var(--theme-foreground)]',
    h3: 'text-heading-3 font-heading-3 text-[var(--theme-foreground)]',
    body: 'text-body font-body text-[var(--theme-foreground)]',
    caption: 'text-caption font-caption text-[var(--theme-secondary)]',
    
    // Button classes
    buttonPrimary: `bg-[var(--theme-primary)] hover:bg-[var(--theme-accent)] text-white border-0`,
    buttonSecondary: `bg-[var(--theme-muted)] hover:bg-[var(--theme-border)] text-[var(--theme-foreground)] border border-[var(--theme-border)]`,
    buttonGhost: `bg-transparent hover:bg-[var(--theme-muted)] text-[var(--theme-foreground)]`,
    
    // Gradients
    gradientPrimary: `bg-gradient-to-r ${theme.gradients.primary}`,
    gradientAccent: `bg-gradient-to-r ${theme.gradients.accent}`,
    gradientHero: `bg-gradient-to-br ${theme.gradients.hero}`,
  };
}

// Theme-aware component wrapper
export function ThemedComponent({ className = '', children, variant = 'default', ...props }) {
  const classes = useSubframeClasses();
  
  const variantClasses = {
    default: '',
    card: classes.card,
    input: classes.input,
    primary: classes.buttonPrimary,
    secondary: classes.buttonSecondary,
    ghost: classes.buttonGhost,
  };
  
  return (
    <div className={`${variantClasses[variant]} ${className}`} {...props}>
      {children}
    </div>
  );
}